/**
 * Каталог Trail Goat: контроли, яких у Horizon немає.
 *
 * Обидва компоненти навмисно не мають власного стану й власних запитів — вони лише
 * керують темовими механізмами:
 *   • <tg-price-slider> пише в поле максимальної ціни фасета й диспатчить на ньому
 *     `change`, після чого працює штатний price-facet-component;
 *   • <tg-load-more> смикає публічний метод results-list, тобто той самий фетчер,
 *     що й нескінченний скрол, разом із його кешем сторінок і префетчем.
 */

/** Форматує суму так само, як макет: розряди нерозривними пробілами, гривня в кінці. */
function formatMoney(value) {
  return `${Math.round(value).toLocaleString('uk-UA').replace(/ /g, ' ')} ₴`;
}

/**
 * Двосторонній повзунок ціни поверх темових числових полів фасета.
 * Розмітку дає snippets/price-filter.liquid.
 */
class TgPriceRange extends HTMLElement {
  connectedCallback() {
    this.min = this.querySelector('.tg-price-range__input--min');
    this.max = this.querySelector('.tg-price-range__input--max');
    if (!this.min || !this.max) return;

    for (const input of [this.min, this.max]) {
      input.addEventListener('input', this.#onInput);
      input.addEventListener('change', this.#onChange);
    }

    // Зворотний зв'язок: числові поля фасета лишаються повноцінним контролом,
    // тож набране в них має рухати повзунки, а не лише навпаки.
    for (const field of this.#fields) field?.addEventListener('change', this.#onFieldChange);

    // Після фільтрації секція приходить із сервера й накладається через morph, а той
    // ПЕРЕВИКОРИСТОВУЄ вузли: `connectedCallback` більше не спрацьовує, і намальований
    // відрізок лишився б від попереднього стану, хоч атрибути вже нові. Тому стежимо
    // за самими атрибутами.
    // Атрибут `value` після взаємодії вже не тягне за собою властивість (інпут
    // «забруднений»), тож зводимо їх руками, інакше повзунок лишиться там, де його
    // покинув користувач, а не там, куди його поставив сервер.
    this.observer = new MutationObserver((records) => {
      for (const record of records) {
        const input = record.target;
        const fromServer = input.getAttribute('value');
        if (fromServer !== null) input.value = fromServer;
      }
      this.#paint();
    });
    for (const input of [this.min, this.max]) {
      this.observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    }

    this.#paint();
  }

  disconnectedCallback() {
    for (const input of [this.min, this.max]) {
      input?.removeEventListener('input', this.#onInput);
      input?.removeEventListener('change', this.#onChange);
    }
    for (const field of this.#fields) field?.removeEventListener('change', this.#onFieldChange);
    this.observer?.disconnect();
  }

  get #fields() {
    const facet = this.closest('price-facet-component');
    return [facet?.querySelector('[ref="minInput"]'), facet?.querySelector('[ref="maxInput"]')];
  }

  get bounds() {
    return { floor: Number(this.min.min), ceil: Number(this.min.max), step: Number(this.min.step) || 1 };
  }

  /**
   * Не даємо повзункам перетнутись: той, що тягнуть, впирається в сусіда,
   * лишаючи між ними один крок.
   */
  #clamp(moved) {
    const { step } = this.bounds;
    const lo = Number(this.min.value);
    const hi = Number(this.max.value);
    if (lo <= hi - step) return;

    if (moved === this.min) this.min.value = String(Math.max(Number(this.min.min), hi - step));
    else this.max.value = String(Math.min(Number(this.max.max), lo + step));
  }

  /** Малює вибраний відрізок. Запитів не робить. */
  #paint() {
    const { floor, ceil } = this.bounds;
    const span = ceil - floor || 1;
    const lo = Number(this.min.value);
    const hi = Number(this.max.value);

    this.style.setProperty('--tg-range-start', `${((lo - floor) / span) * 100}%`);
    this.style.setProperty('--tg-range-end', `${((hi - floor) / span) * 100}%`);
  }

  /** Синхронізує повзунки з числовими полями і навпаки. */
  #syncFieldsFromSlider() {
    const [minField, maxField] = this.#fields;
    const { floor, ceil } = this.bounds;
    if (minField) minField.value = Number(this.min.value) <= floor ? '' : this.min.value;
    if (maxField) maxField.value = Number(this.max.value) >= ceil ? '' : this.max.value;
    return maxField;
  }

  #onFieldChange = () => {
    const [minField, maxField] = this.#fields;
    const { floor, ceil } = this.bounds;
    const toNumber = (field, fallback) => {
      const digits = (field?.value || '').replace(/[^\d]/g, '');
      return digits === '' ? fallback : Number(digits);
    };

    this.min.value = String(Math.max(floor, Math.min(ceil, toNumber(minField, floor))));
    this.max.value = String(Math.max(floor, Math.min(ceil, toNumber(maxField, ceil))));
    this.#paint();
  };

  #onInput = (event) => {
    this.#clamp(event.target);
    this.#paint();
    this.#syncFieldsFromSlider();
  };

  /**
   * Відпустили — віддаємо значення темовому фасету. Межа діапазону пишеться
   * порожнім рядком: інакше в URL осідав би фільтр, який нічого не відсікає.
   */
  #onChange = (event) => {
    this.#clamp(event.target);
    this.#paint();

    const maxField = this.#syncFieldsFromSlider();
    maxField?.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

if (!customElements.get('tg-price-range')) {
  customElements.define('tg-price-range', TgPriceRange);
}

/**
 * Кнопка «Показати ще» замість нескінченного скролу.
 * Розмітку дає snippets/tg-load-more.liquid.
 */
class TgLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.tg-load-more__button');
    this.status = this.querySelector('.tg-load-more__status');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  get list() {
    return this.closest('results-list');
  }

  #onClick = async () => {
    const list = this.list;
    if (!list || typeof list.renderNextPage !== 'function') return;

    this.button.setAttribute('aria-busy', 'true');
    await list.renderNextPage();
    this.button.removeAttribute('aria-busy');
    this.#refresh();
  };

  /** Перемальовує підпис і ховає кнопку, коли показані всі товари. */
  #refresh() {
    const grid = this.list?.querySelector('[ref="grid"]');
    if (!grid) return;

    const shown = grid.querySelectorAll('[ref="cards[]"]').length;
    const total = Number(this.dataset.total || 0);

    if (this.status && this.dataset.template) {
      this.status.textContent = this.dataset.template
        .replace('@shown@', String(shown))
        .replace('@total@', String(total));
    }

    const lastPage = Number(grid.dataset.lastPage || 1);
    const currentPage = Number(grid.lastElementChild?.dataset.page || 1);
    if (shown >= total || currentPage >= lastPage) this.hidden = true;
  }
}

if (!customElements.get('tg-load-more')) {
  customElements.define('tg-load-more', TgLoadMore);
}

/**
 * «Скинути» повертає панелі фасетів у стартовий стан (згорнуто все, крім брендів).
 *
 * Рішення тут немає й бути не повинно: який фасет відкритий у «чистому» стані,
 * вирішує Liquid і віддає атрибутом `data-tg-pristine-open` на кожному `<details>`
 * (snippets/list-filter.liquid). Скрипт лише переписує стан із цього атрибута.
 *
 * Робити це треба СИНХРОННО на кліку, а не після перемальовки: `morph` переносить
 * `details.open` зі СТАРОГО вузла в новий (assets/morph.js), тож стан, виставлений
 * до фетчу, переживе відповідь сервера. Дочекатися перемальовки й виставити стан
 * після неї теж можна було б, але події «секцію оновлено» тема не дає.
 *
 * Панелі беремо по всьому документу навмисно: сайдбар і мобільна шторка — дві
 * незалежні копії фасетів, і скидання в одній має лягти на обидві, інакше шторка
 * відкрилась би зі старим станом.
 */
function resetFacetPanels() {
  for (const panel of document.querySelectorAll('details[data-tg-pristine-open]')) {
    panel.open = panel.dataset.tgPristineOpen === 'true';
  }
}

// Делеговано, бо самі кнопки приїжджають із сервера й після кожної перемальовки
// це вже інші вузли. `.facets__clear-all-link` — сайдбар, `.facets__clear-all` — шторка.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('.facets__clear-all-link, .facets__clear-all')) resetFacetPanels();
});
